import AsyncStorage from "@react-native-async-storage/async-storage";
import dayjs from "dayjs";
import { Link, router } from "expo-router";
import { useField, useForm } from "mobx-easy-form";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef } from "react";
import { Keyboard, StyleSheet } from "react-native";
import * as yup from "yup";
import { Button } from "~/components/Button";
import { Collapsible } from "~/components/Collapsible";
import { Pressable } from "~/components/Pressable";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { TextInput } from "~/components/TextInput";
import { View } from "~/components/View";
import { useAlert } from "~/hooks/useAlert";
import { state } from "~/store";
import { styleConstants } from "~/style/styleConstants";
import { promptYesNo } from "~/utils/promptYesNo";
import { GenderFemale, GenderMale, GenderOther } from "../gender-icons";
import { FormattedMessage, getLocaleURL } from "../intl";
import { useResponsive } from "../live-chat/useResponsive";
import type {
  TextInput as RNTextInput,
  StyleProp,
  TextStyle,
} from "react-native";

export const SectionForm = observer(function SectionForm() {
  const alert = useAlert();
  const r = useResponsive();

  const form = useForm({
    async onSubmit(formData) {
      Keyboard.dismiss();
      // We persist the raw string values to storage, so we can use them in the future
      await AsyncStorage.setItem("name", formData.rawValues.name);
      await AsyncStorage.setItem("age", formData.rawValues.age);
      await AsyncStorage.setItem("sex", formData.rawValues.sex);

      state.localUserDetails.name = formData.values.name;
      state.localUserDetails.age = formData.values.age;
      state.localUserDetails.sex = formData.values.sex;

      const message = (
        <Text alignLeft>
          {intl.formatMessage(
            {
              defaultMessage:
                "By using this video chat, you agree to our <link>guidelines</link>! Here are some rules to ensure a great experience:{br}{br}<bold>Be Respectful:</bold> Treat others with kindness and courtesy.{br}<bold>Keep Your Face Visible:</bold> Ensure your face is visible on camera.{br}<bold>No Harassment:</bold> Any form of harassment or disrespectful behavior will not be tolerated.{br}<bold>Follow Community Guidelines:</bold> Familiarize yourself with our <link>community guidelines</link> and abide by them.{br}{br}Let's create a friendly and safe space for everyone to enjoy!",
              id: "3WcUzW",
              description:
                "Message for the alert that asks user to start a live video chat",
            },
            {
              br: "\n",
              bold: (chunks) => <Text weightExtraBold>{chunks}</Text>,
              link: (chunks) => (
                <Link href="https://bokbok.io/guidelines" target="_blank">
                  <Text colorTheme weightExtraBold>
                    {chunks}
                  </Text>
                </Link>
              ),
            },
          )}
        </Text>
      );

      const shouldContinue = await promptYesNo(
        {
          title: intl.formatMessage({
            defaultMessage: "Rules",
            id: "80eH0x",
            description:
              "Title for the alert that asks user to read the rules before starting a live video chat",
          }),
          // @ts-expect-error promptYesNo should be able to handle <Text> elements, and not just strings, but it doesn't currently
          message: message,
          yesText: intl.formatMessage({
            defaultMessage: "Accept",
            id: "D7ViN1",
            description: "Text for accept button in overlay",
          }),
          noText: intl.formatMessage({
            defaultMessage: "Cancel",
            id: "7tsY9l",
            description: "Cancel button in alert",
          }),
        },
        alert,
      );

      if (!shouldContinue) return;

      router.navigate(getLocaleURL("/video-settings"));
    },
  });

  const name = useField<string, string>({
    form,
    id: "name",
    initialValue: "",
    validationSchema: yup.string().required().trim().min(1).max(64),
  });

  const age = useField<string, number>({
    form,
    id: "age",
    initialValue: "",
    validate(value) {
      if (value === "") {
        return { error: "Date of birth is required" };
      }
      const date = dayjs(value, "DD/MM/YYYY", true);
      if (date.isValid() === false) {
        return { error: "Date should be in format DD/MM/YYYY" };
      }

      const age = dayjs().diff(date, "years");

      if (age < 18) {
        return { error: "You must be 18 or older" };
      }

      if (age > 100) {
        return { error: "You must be less than 100 years old" };
      }

      return { parsed: age };
    },
  });

  const sex = useField<
    undefined | "male" | "female" | "other",
    "male" | "female" | "other"
  >({
    form,
    id: "sex",
    initialValue: undefined,
    validationSchema: yup
      .string()
      .oneOf(["male", "female", "other"])
      .required(),
  });

  const isMaleGenderSelected = sex.state.value === "male";
  const isFemaleGenderSelected = sex.state.value === "female";
  const isOtherGenderSelected = sex.state.value === "other";

  const dateOfBirthRef = useRef<RNTextInput>(null);

  useEffect(
    function populateUserDetailsFromPersistanceEffect() {
      async function runPopulateUserDetailsFromPersistanceEffect() {
        const schema = yup
          .object({
            name: yup.string().required(),
            age: yup
              .string()
              .matches(/^\d\d\/\d\d\/\d\d\d\d$/)
              .default("")
              .required(),
            sex: yup.string().oneOf(["male", "female", "other"]).required(),
          })
          .required();

        const rawUserDetails = {
          name: await AsyncStorage.getItem("name"),
          age: await AsyncStorage.getItem("age"),
          sex: await AsyncStorage.getItem("sex"),
        };

        try {
          const userDetails = schema.validateSync(rawUserDetails);
          name.actions.onChange(userDetails.name);
          age.actions.onChange(userDetails.age);
          sex.actions.onChange(userDetails.sex);
        } catch (error) {
          console.log(
            "Invalid user details in persisted storage.",
            JSON.stringify(rawUserDetails),
            error,
          );
        }
      }

      runPopulateUserDetailsFromPersistanceEffect();
    },
    [age, name, sex],
  );

  return (
    <View>
      {/* Start section - Upper part with title and descriptions */}
      <View
        paddingExtraLarge={r(false, true, false)}
        paddingSmall={r(true, false, false)}
        colorBackgroundLightDark={r(true, true, false)}
      >
        <Text
          style={
            {
              fontSize: r(24, 48, 72),
              lineHeight: "100%" as any,
              textWrap: "balance", // Works only on newer browsers
            } as StyleProp<TextStyle>
          }
          colorDark
          weightExtraBold
        >
          {intl.formatMessage(
            {
              defaultMessage: "Meet & <themeColor>Live</themeColor> Video Chat",
              id: "Yq0xSu",
              description: "Main title on the webpage",
            },
            {
              themeColor(chunk) {
                return (
                  <Text
                    style={{
                      fontSize: r(24, 48, 72),
                      lineHeight: "100%" as any,
                    }}
                    colorTheme={r(false, true, true)}
                    weightExtraBold
                  >
                    {chunk}
                  </Text>
                ) as any;
              },
            },
          )}
        </Text>
        <Text
          colorDark
          sizeExtraLarge
          weightExtraBold
          style={{
            display: r("none", "flex", "flex"),
            paddingTop: styleConstants.spacingLarge,
          }}
        >
          {intl.formatMessage({
            id: "Je7urm",
            defaultMessage: "Meet New People & Friends on BokBok",
            description: "Meet New People & Friends on BokBok",
          })}
        </Text>
        <Spacer medium />
        <Text colorDark sizeMedium>
          {intl.formatMessage({
            defaultMessage:
              "No registration required for BokBok's video chat - just enter your name, birth date and gender to instantly start live video chatting.",
            id: "J7q6Ww",
            description: "Description on main webpage",
          })}
        </Text>
      </View>
      {/* End section - Upper part with title and descriptions */}

      {/* Start section - Form */}
      <View
        paddingHorizontalSmall={r(true, false, false)}
        alignSelfCenter={r(true, true, false)}
        style={{
          maxWidth: 480,
          width: "100%",
        }}
      >
        <Spacer medium />

        <TextInput
          label={intl.formatMessage({
            defaultMessage: "Name",
            id: "HOvzK4",
            description: "Text input label",
          })}
          placeholder={intl.formatMessage({
            id: "PDbi5a",
            defaultMessage: "Your name",
            description: "Placeholder on TextInput for entering name",
          })}
          value={name.state.value}
          maxLength={64}
          onChangeText={name.actions.onChange}
          onBlur={() => {
            name.actions.onChange(name.state.value.trim());
            name.actions.onBlur();
          }}
          caption={name.computed.ifWasEverBlurredThenError}
          error={!!name.computed.ifWasEverBlurredThenError}
          blurOnSubmit={false}
          autoCapitalize="words"
          autoCorrect={false}
          autoComplete="name"
          enterKeyHint="next"
          spellCheck={false}
          onSubmitEditing={() => {
            dateOfBirthRef.current?.focus();
          }}
        />

        <Spacer large />

        <TextInput
          ref={dateOfBirthRef}
          label={intl.formatMessage({
            defaultMessage: "Date of birth (DD/MM/YYYY)",
            id: "VVkddC",
            description: "Text input label",
          })}
          placeholder={intl.formatMessage({
            id: "70+KGL",
            defaultMessage: "Date of birth",
            description: "Placeholder on date of birth input",
          })}
          maxLength={10}
          keyboardType="numeric"
          blurOnSubmit
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="off"
          enterKeyHint="next"
          spellCheck={false}
          value={age.state.value}
          onBlur={age.actions.onBlur}
          onFocus={age.actions.onFocus}
          caption={
            age.state.wasEverBlurred || age.state.value !== ""
              ? age.computed.error
              : undefined
          }
          error={
            age.state.wasEverBlurred || age.state.value !== ""
              ? !!age.computed.error
              : false
          }
          onChangeText={(value) => {
            if (value.length > 10) {
              return;
            }

            if (age.state.value.length < value.length && /^\d\d$/.test(value)) {
              age.actions.onChange(value + "/");
              return;
            }

            // When user types in a month without the leading 0 we prefill it for them
            if (
              age.state.value.length < value.length &&
              /^\d\d\/\d$/.test(value) &&
              Number(value[3]) > 1
            ) {
              age.actions.onChange(value.slice(0, 3) + "0" + value[3] + "/");
              return;
            }

            // In the case when they type the slash "DD/1/"
            if (
              age.state.value.length < value.length &&
              /^\d\d\/\d\/$/.test(value)
            ) {
              age.actions.onChange(value.slice(0, 2) + "/0" + value[3] + "/");
              return;
            }

            // In the case when they type the slash "DD/MM/9"
            if (
              age.state.value.length < value.length &&
              /^\d\d\/\d\d\/\d$/.test(value) &&
              Number(value[6]) > 3
            ) {
              age.actions.onChange(value.slice(0, 6) + "19" + value[6]);
              return;
            }

            if (
              age.state.value.length < value.length &&
              /^\d\d\/\d\d$/.test(value)
            ) {
              age.actions.onChange(value + "/");
              return;
            }

            age.actions.onChange(value);
          }}
        />

        <Spacer large />

        <Text weightSemiBold>
          {intl.formatMessage({
            id: "u1GShg",
            defaultMessage: "Your gender",
            description: "Text input label",
          })}
        </Text>

        <Spacer small />

        {/* Gender start */}
        <View
          borderRadiusSmall
          flexDirectionRow
          style={[S.genderContainer, { gap: 8 }]}
        >
          <Pressable
            style={S.button}
            flex
            centerContent
            colorBackgroundTheme={isFemaleGenderSelected}
            colorBackgroundWhite={!isFemaleGenderSelected}
            onPress={() => {
              Keyboard.dismiss();
              sex.actions.onChange("female");
            }}
          >
            <GenderFemale />

            <Text colorLight={isFemaleGenderSelected}>
              <FormattedMessage
                id="qEsNNX"
                defaultMessage="Female"
                description="Option in CreateProfileGenderScreen if you are a Female you can select this option"
              />
            </Text>
          </Pressable>

          <Pressable
            style={S.button}
            flex
            centerContent
            colorBackgroundTheme={isMaleGenderSelected}
            colorBackgroundWhite={!isMaleGenderSelected}
            onPress={() => {
              Keyboard.dismiss();
              sex.actions.onChange("male");
            }}
          >
            <GenderMale />

            <Text colorLight={isMaleGenderSelected}>
              <FormattedMessage
                id="Ve64ij"
                defaultMessage="Male"
                description="Option in CreateProfileGenderScreen. If your gender is male you can select this option"
              />
            </Text>
          </Pressable>

          <Pressable
            style={S.button}
            flex
            centerContent
            colorBackgroundTheme={isOtherGenderSelected}
            colorBackgroundWhite={!isOtherGenderSelected}
            onPress={() => {
              Keyboard.dismiss();
              sex.actions.onChange("other");
            }}
          >
            <GenderOther />

            <Text colorLight={isOtherGenderSelected}>
              <FormattedMessage
                id="36/11/"
                defaultMessage="Other"
                description="Option in CreateProfileGenderScreen. If your gender is not male or female you can select this option."
              />
            </Text>
          </Pressable>
        </View>
        {/* Gender end */}
        <Collapsible collapsed={!sex.computed.ifWasEverFocusedThenError}>
          <Spacer small />
          <Text
            sizeMedium
            colorDanger={!!sex.computed.ifWasEverFocusedThenError}
            weightRegular
          >
            {sex.computed.ifWasEverFocusedThenError}
          </Text>
        </Collapsible>

        <View style={{ height: r(32, 38, 38) }} />

        <Button
          colorTheme
          style={{ height: r(48, 62, 52) }}
          onPress={form.actions.submit}
        >
          <Text sizeExtraLarge weightExtraBold colorLight>
            <FormattedMessage
              id="yo1vLg"
              defaultMessage="Let's go!"
              description="Let's go! - text on the live video data form screen"
            />
          </Text>
        </Button>
      </View>
      {/* End section - Form */}
    </View>
  );
});

const S = StyleSheet.create({
  genderContainer: {
    borderColor: styleConstants.colorTextDarkSoft,
  },
  button: {
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: styleConstants.colorBackgroundGray,
    borderRadius: 4,
  },
});
